import { useEffect, useRef, useState } from 'react';

import { FunktionsSymbol, type FunktionsIcon } from './FunktionsTaste';
import { useFokusfalleAmFenster } from './zahlung-v2/fokus';

/**
 * Rückfrage vor einer folgenreichen Aktion (POS V2, freigegebener Rahmen D11 `18:3810`,
 * Phase 2): Titel als Frage, Folgen in einem Satz, links die sichere Wahl („Behalten“,
 * „Abbrechen“, „Weiter warten“ – Esc, Startfokus), rechts die Aktion in Gefahrfarbe.
 *
 * Nur Darstellung: Die Aktion selbst (Entfernen, Leeren, Zahlungsabbruch) bleibt unverändert und
 * läuft erst nach „bestätigen“. Tippen auf die Abdunkelung schließt nicht (§ 3.1).
 */
export function Rueckfrage(props: {
  titel: string;
  /** Symbol vor dem Titel (bestehende Strich-Symbole der Funktionsleiste). */
  icon?: FunktionsIcon;
  text: string;
  /** Beschriftung der sicheren Wahl (Esc). */
  zurueck: string;
  /** Beschriftung der Aktion. */
  bestaetigen: string;
  onZurueck: () => void;
  onBestaetigen: () => void;
}) {
  const dialog = useRef<HTMLDivElement | null>(null);
  const sicher = useRef<HTMLButtonElement | null>(null);
  // Codex UI-Abnahme 01.11.: Auslöser beim ersten Rendern merken (vor der Fokusfalle) und beim
  // Schließen zurückfokussieren, sofern er noch da ist und der Fokus nicht schon woanders liegt.
  const [ausloeser] = useState(() => {
    const a = document.activeElement;
    return a instanceof HTMLElement && a !== document.body ? a : null;
  });
  useFokusfalleAmFenster(dialog, true);
  const zurueck = useRef(props.onZurueck);
  zurueck.current = props.onZurueck;
  useEffect(() => {
    sicher.current?.focus();
    // Erfassungsphase: Esc erreicht die Kürzel des Verkaufs dahinter nicht.
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        zurueck.current();
      }
    };
    window.addEventListener('keydown', beiTaste, true);
    return () => {
      window.removeEventListener('keydown', beiTaste, true);
      const jetzt = document.activeElement;
      if (
        ausloeser?.isConnected === true &&
        (jetzt === null || jetzt === document.body || dialog.current?.contains(jetzt) === true)
      ) {
        ausloeser.focus();
      }
    };
  }, [ausloeser]);
  return (
    <div
      ref={dialog}
      className="zahlung-dialog rueckfrage"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="rueckfrage-titel"
      aria-describedby="rueckfrage-text"
      tabIndex={-1}
      style={{ outline: 'none' }}
    >
      <div className="karte dlg">
        <h2 id="rueckfrage-titel">
          {props.icon !== undefined && <FunktionsSymbol icon={props.icon} groesse={22} />}
          {props.titel}
        </h2>
        <p id="rueckfrage-text">{props.text}</p>
        <div className="dlg-fuss">
          <button
            ref={sicher}
            type="button"
            className="taste zweit"
            data-kuerzel="Esc"
            onClick={props.onZurueck}
          >
            {props.zurueck}
          </button>
          <button type="button" className="taste gefahr" onClick={props.onBestaetigen}>
            {props.bestaetigen}
          </button>
        </div>
      </div>
    </div>
  );
}
